<!-- 講座画面、メンバー一覧選択時のページ -->
<script setup lang="ts">
  import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    Title,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
  } from "chart.js";
  import { useDisplay } from "vuetify";
  import { Bar, Doughnut, Line } from "vue-chartjs";
  import { PlanInfo } from "~/entities/PlanInfo";
  import { usePlanStore } from "~/stores/PlanStore";

  interface Props {
    planInfo: PlanInfo;
  }
  const localProps = withDefaults(defineProps<Props>(), {});

  defineEmits(["onBack"]);

  const planPriceId = ref<number>(0);
  const planPriceList = computed(() => {
    return [{ planPriceTitle: "全て", planPriceId: 0 }, ...statisticData.value.planPriceList];
  });

  ChartJS.register(ArcElement, Tooltip, Legend);
  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
  ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

  const planStore = usePlanStore();

  const loading = computed<boolean>(() => planStore.loadingStatistics);

  // レスポンシブ対応
  const { name, width: screenWidth } = useDisplay();
  const isSp = computed<boolean>((): boolean => name.value === "xs");

  const statisticData = computed(() => {
    const responseData = planStore.contractStatisticResponse;
    return {
      contractDaysCount: responseData?.contractDaysCount || [],
      activeContractCount: responseData?.activeContractCount || [],
      newContractCount: responseData?.newContractCount || [],
      withdrawalCount: responseData?.withdrawalCount || [],
      talkCounts: responseData?.talkCounts || [],
      totalActiveContractCount: responseData?.totalActiveContractCount || 0,
      totalNewContractCount: responseData?.totalNewContractCount || 0,
      totalWithdrawalCount: responseData?.totalWithdrawalCount || 0,
      totalPostCount: responseData?.totalPostCount || 0,
      totalPostUserCount: responseData?.totalPostUserCount || 0,
      planPriceList: responseData?.planPriceList || [],
    };
  });

  const chartData = computed(() => {
    if (loading.value || !planStore.contractStatisticResponse) {
      return undefined;
    }

    const { contractDaysCount, activeContractCount, newContractCount, withdrawalCount, talkCounts } =
      statisticData.value;

    // 契約日数
    const [h, s] = hexToHSL("#119CF6");
    const contractData = {
      labels: contractDaysCount.map((item: any) => item.period),
      datasets: [
        {
          backgroundColor: contractDaysCount.map((item, index) => {
            const l = 0.5 + (index % 5) * 0.1 - 0.2;
            return hslToHex(h, s, l);
          }),
          data: contractDaysCount.map((item: any) => item.count),
          borderWidth: contractDaysCount.map(() => 0),
        },
      ],
    };

    // 会員推移
    const memberData = {
      labels: activeContractCount.map((item: any) => item.countDate),
      datasets: [
        {
          label: "会員数",
          backgroundColor: "#0E7DC5",
          borderColor: "#0E7DC5",
          data: activeContractCount.map((item: any) => item.count),
        },
        {
          label: "新規入会数",
          backgroundColor: "#A0D7FB",
          borderColor: "#A0D7FB",
          data: newContractCount.map((item: any) => item.count),
        },
        {
          label: "退会者数",
          backgroundColor: "#CFEBFD",
          borderColor: "#CFEBFD",
          data: withdrawalCount.map((item: any) => item.count),
        },
      ],
    };

    // 投稿数
    const postData = {
      labels: talkCounts.map((item: any) => item.talkPostDate),
      datasets: [
        {
          label: "投稿数",
          backgroundColor: "#119CF6",
          data: talkCounts.map((item: any) => item.talkPostCount),
        },
        {
          label: "投稿者数",
          backgroundColor: "#A0D7FB",
          data: talkCounts.map((item: any) => item.talkPostUserCount),
        },
      ],
    };
    return {
      contractData,
      memberData,
      postData,
    };
  });

  const chartOptions = {
    responsive: false,
    maintainAspectRatio: false,
    legend: {
      labels: {
        fontSize: isSp.value ? 32 : 22,
      },
    },
  };

  // 講座ID
  const planId = computed<string>(() => {
    return (useRoute().query.planId as string) || localProps.planInfo.id;
  });

  // データ取得量
  const dataVolume = computed<string>(() => {
    return (useRoute().query.dataVolume as string) || "allData";
  });

  // トランジションエフェクトの関係でデータ取得は外部から行う
  const fetch = () => {
    const options = { plan_id: "" };
    if (dataVolume.value !== "allData") {
      options.plan_id = planId.value;
    }
    planStore.fetchStatistics([planPriceId.value], { ...options });
  };

  watch(
    () => {
      return planPriceId.value;
    },
    (newValue, _oldValue) => {
      planStore.fetchStatistics([newValue], { plan_id: planId.value });
    },
  );

  watch(
    () => {
      return planId.value;
    },
    (_newValue, _oldValue) => {
      planPriceId.value = 0;
    },
  );

  watch(
    () => {
      return dataVolume.value;
    },
    (_newValue, _oldValue) => {
      fetch();
    },
  );

  const getGraphWidth = () => {
    const width = 390;
    if (screenWidth.value - 20 < width) {
      return `${screenWidth.value - 20}px`;
    }
    return `${width}px`;
  };

  defineExpose({
    fetch,
    loading,
  });
</script>
<template>
  <MoleculesContainerMain
    title="統計"
    :show-plans="true"
    :default-plan-id="planInfo.id"
    :loading="loading"
    svg-icon="assets/images/icon/member-statistic.svg"
    @on-back="$emit('onBack')"
  >
    <div class="mx-2 my-2">
      <template v-if="loading || !chartData">
        <v-row class="chart-container">
          <v-col cols="12">
            <div class="pa-4"><v-skeleton-loader height="100" class="mb-4" /></div>
          </v-col>
          <v-col cols="12">
            <div class="pa-4"><v-skeleton-loader height="100" class="mb-4" /></div>
          </v-col>
          <v-col cols="12">
            <div class="pa-4"><v-skeleton-loader height="100" class="mb-4" /></div>
          </v-col>
        </v-row>
      </template>
      <template v-else>
        <v-row class="chart-container">
          <v-col v-if="statisticData.contractDaysCount.length > 0" cols="12" class="pb-4">
            <div>
              <div class="card-title">
                <span class="card-title-text">契約日数</span>
              </div>

              <div class="card-chart">
                <Doughnut
                  class="mx-auto"
                  :style="{ width: getGraphWidth() }"
                  :data="chartData.contractData"
                  :options="chartOptions"
                />
              </div>
            </div>
          </v-col>

          <v-col cols="12" class="pb-4">
            <div>
              <div class="card-title">
                <span class="card-title-text">会員推移</span>
                <v-menu location="bottom">
                  <template #activator="{ props }">
                    <v-btn variant="tonal" size="large" class="px-2" v-bind="props">
                      <span>
                        プラン別({{
                          planPriceList.find(item => item.planPriceId == planPriceId)?.planPriceTitle || "すべて"
                        }})
                      </span>
                      <v-icon icon="$mdiChevronDown" />
                    </v-btn>
                  </template>
                  <v-sheet class="popup">
                    <div class="popup-list">
                      <template v-for="(plan, index) in planPriceList" :key="index">
                        <div class="popup-list-item" @click="planPriceId = plan.planPriceId">
                          <div>
                            <div v-if="plan.planPriceId" class="text-body-2">ID: {{ plan.planPriceId }}</div>
                            <div>{{ plan.planPriceTitle }}</div>
                          </div>
                          <div>{{ planPriceId == plan.planPriceId ? "✔" : "" }}</div>
                        </div>
                        <v-divider v-if="planPriceList.length - 1 != index" />
                      </template>
                    </div>
                  </v-sheet>
                </v-menu>
              </div>

              <div class="card-data">
                <div class="card-data-item">
                  <div class="card-data-item-title">会員数</div>
                  <div class="card-data-item-value">
                    {{ statisticData.totalActiveContractCount || "0" }}
                  </div>
                </div>
                <div class="card-data-item">
                  <div class="card-data-item-title">新規入会数</div>
                  <div class="card-data-item-value">
                    {{ statisticData.totalNewContractCount || "0" }}
                  </div>
                </div>
                <div class="card-data-item">
                  <div class="card-data-item-title">退会者数</div>
                  <div class="card-data-item-value">
                    {{ statisticData.totalWithdrawalCount || "0" }}
                  </div>
                </div>
              </div>

              <div class="card-chart mt-3">
                <Line
                  class="mx-auto"
                  :style="{ width: !isSp ? '768px' : getGraphWidth(), height: '400px' }"
                  :data="chartData.memberData"
                  :options="chartOptions"
                />
              </div>
            </div>
          </v-col>

          <v-col cols="12" class="pb-4">
            <div>
              <div class="card-title">
                <span class="card-title-text">投稿数</span>
              </div>
              <div class="card-data">
                <div class="card-data-item">
                  <div class="card-data-item-title">投稿数</div>
                  <div class="card-data-item-value">
                    {{ statisticData.totalPostCount || "0" }}
                  </div>
                </div>
                <div class="card-data-item">
                  <div class="card-data-item-title">投稿者数</div>
                  <div class="card-data-item-value">
                    {{ statisticData.totalPostUserCount || "0" }}
                  </div>
                </div>
                <div class="card-data-item">
                  <div class="card-data-item-title">投稿数 / 投稿者数</div>
                  <div class="card-data-item-value">
                    {{
                      Number(
                        (statisticData.totalPostCount || 0) / (statisticData.totalPostUserCount || 0) || 0,
                      ).toFixed(1)
                    }}
                  </div>
                </div>
              </div>

              <div class="card-chart mt-3">
                <Bar
                  class="mx-auto"
                  :style="{ width: !isSp ? '768px' : getGraphWidth(), height: '300px' }"
                  :data="chartData.postData"
                  :options="chartOptions"
                />
              </div>
            </div>
          </v-col>
        </v-row>
      </template>
    </div>
  </MoleculesContainerMain>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";
  .chart-container {
    margin: 0 auto;
  }
  .popup {
    background-color: rgb(var(--v-theme-monochrome-04)) !important;
  }
  .popup-list {
    padding: 8px 0;
  }
  .popup-list-item {
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    &:hover {
      background-color: rgb(var(--v-theme-monochrome-05));
    }
  }
  .card-data {
    display: flex;
    // text align center
    justify-content: center;
    gap: 8px;
  }
  .card-data-item {
    padding: 8px;
    width: 150px;
    background-color: rgb(var(--v-theme-monochrome-04)) !important;
  }
  .card-data-item-title {
    font-size: 14px;
    font-weight: 700;
    color: #8a8a8a;
    text-align: center;
  }
  .card-data-item-value {
    font-size: 18px;
    font-weight: 700;
    text-align: center;
  }
  .card-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 10px;
  }
  .card-title-text {
    font-size: 20px;
    font-weight: 700;
  }
</style>
